// src/pages/Proposals/ProposalManagement.js
import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, Drawer, TextField } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  fetchProposals,
  searchProposals,
  exportProposalsById,
  filterProposals,
  deleteProposals,
} from "api/proposalApi";
import { GridToolbar } from "@mui/x-data-grid";
import { enqueueSnackbar } from "notistack";

import { useNavigate } from "react-router-dom";
import ProposalFilterSidebar from "./ProposalFilterSidebar";

import { getUserInfo } from "../../utils/userUtils";

// Página de crear y eliminar propuesta
import CreateProposalDialog from "./CreateProposalDialog";
import DeleteProposalDialog from "./DeleteProposalDialog";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const ProposalManagement = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState(""); // lo que se aplica al DataGrid
  const [filteredProposals, setFilteredProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [selectedProposals, setSelectedProposals] = useState([]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const navigate = useNavigate();

  // Crear propuesta
  const [createOpen, setCreateOpen] = useState(false); // <--- define aquí
  const [proposals, setProposals] = useState([]); // lista de proposals
  const handleCreated = async () => {
    await loadProposals();
  };
  const token = localStorage.getItem("token");
  const userInfo = getUserInfo();

  // Delete proposals empty
  const [deleteOpen, setDeleteOpen] = useState(false);
  const selectedProposalObjects = proposals.filter((p) =>
    selectedProposals.includes(p.id),
  );
  const handleDelete = async () => {
    const proposalsToDelete = proposals.filter((p) =>
      selectedProposals.includes(p.id),
    );

    const ids = proposalsToDelete.map((p) => p.id);

    const results = await deleteProposals(ids);

    const success = results.filter((r) => r.status === "fulfilled").length;

    const failed = results.filter((r) => r.status === "rejected").length;

    if (success > 0) {
      enqueueSnackbar(`${success} proposal(s) deleted successfully`, {
        variant: "success",
      });
    }

    if (failed > 0) {
      enqueueSnackbar(`${failed} proposal(s) could not be deleted`, {
        variant: "warning",
      });
    }

    await loadProposals();
    setDeleteOpen(false);
  };

  // Load Proposals
  const loadProposals = async () => {
    try {
      const data = await fetchProposals();
      setProposals(data);
      setFilteredProposals(data);
      console.log("puedo ver", data);
    } catch (err) {
      console.error("Error loading proposals", err);
    }
  };

  // Export selected proposals and trigger download
  const handleExportSelected = async () => {
    try {
      const proposalsToExport = proposals.filter((p) =>
        selectedProposals.includes(p.id),
      );
      const ids = proposalsToExport.map((p) => p.id);

      // Aquí asumes que la respuesta es un Blob tipo ZIP
      const response = await exportProposalsById(ids);

      const blob = new Blob([response], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[-:T]/g, "");
      link.href = url;
      link.setAttribute("download", `proposals_metadata.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      // Si la respuesta es un blob (como un error HTML o JSON), intenta leerlo
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text(); // leer el blob como texto
          console.error("Backend error message:", text);
        } catch (e) {
          console.error("Error reading blob response:", e);
        }
      }
      console.error("Error exporting selected proposals", err);
    }
  };

  // Filter Proposals
  const fetchProposalsWithFilters = async () => {
    try {
      const data = await filterProposals(filters);
      setProposals(data || []);
      setFilteredProposals(data || []); // también actualiza el listado visible
    } catch (error) {
      console.error("Error fetching filtered proposals:", error);
    }
  };

  // UseEffect to load proposals on component mount
  useEffect(() => {
    loadProposals();
  }, []);

  // useEffect to update filtered proposals when appliedSearch changes
  useEffect(() => {
    if (appliedSearch.trim() === "") {
      setFilteredProposals(proposals);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      try {
        const results = await searchProposals(appliedSearch.trim());
        setFilteredProposals(results);
      } catch (err) {
        console.error("Error searching proposals:", err);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [appliedSearch, proposals]);

  // Define DataGrid columns
  const columns = [
    // { field: "id", headerName: "ID", width: 10 },

    { field: "code", headerName: "Code", flex: 0.6 },
    { field: "title", headerName: "Title", flex: 1 },
    // { field: "abstract", headerName: "Abstract", flex: 2 },
    {
      field: "created_at",
      headerName: "Metadata date",
      hidden: true,
      sortable: true,
      valueGetter: (params) => {
        const rawValue = params;
        return rawValue ? new Date(rawValue) : null;
      },
      valueFormatter: (params) => {
        const value = params;
        if (!(value instanceof Date)) return "—";
        return value.toISOString().slice(0, 10); // YYYY-MM-DD
      },
    },

    // {
    //   field: "submission_date",
    //   headerName: "Sub. Date",
    //   type: "date",
    //   flex: 0.45,
    //   valueGetter: (params) => {
    //     const rawValue = params;
    //     return rawValue ? new Date(rawValue) : null;
    //   },
    //   valueFormatter: (params) => {
    //     const value = params;
    //     if (!(value instanceof Date)) return "—";
    //     return value.toISOString().slice(0, 10); // YYYY-MM-DD
    //   },
    // },
    // {
    //   field: "institution",
    //   headerName: "Node",
    //   flex: 0.4,
    //   valueGetter: (params) => {
    //     return params?.alias ?? params?.name ?? "—";
    //   },
    // },
    {
      field: "infrastructure",
      headerName: "Infrastructure",
      flex: 0.5,
      valueGetter: (params) => {
        return params?.name ?? "—";
      },
    },
    {
      field: "beamlines",
      headerName: "Beamlines",
      flex: 0.6,
      valueGetter: (params) => {
        const beamlines = params;
        if (!Array.isArray(beamlines)) return "—";
        return beamlines.map((b) => b?.code ?? "").join(", ");
      },
    },
    {
      field: "techniques",
      headerName: "Techniques",
      flex: 0.8,
      valueGetter: (params) => {
        const techniques = params;
        if (!Array.isArray(techniques)) return "—";
        return techniques.map((t) => t?.alias ?? "").join(", ");
      },
    },
    {
      field: "pi",
      headerName: "PI",
      flex: 0.7,
      valueGetter: (params) => {
        const pi = params;
        return pi?.name && pi?.surname ? `${pi.name} ${pi.surname}` : "—";
      },
    },
    // {
    //   field: "researchers",
    //   headerName: "Researchers",
    //   flex: 1,
    //   valueGetter: (params, row) => {
    //     if (row.source === "manual") {
    //       return row.participants_text || "—";
    //     }

    //     const researchers = params;
    //     if (!Array.isArray(researchers)) return "—";

    //     return researchers
    //       .map((r) => `${r?.name ?? ""} ${r?.surname ?? ""}`)
    //       .join(", ");
    //   },
    // },
    // {
    //   field: "projects",
    //   headerName: "Projects",
    //   flex: 0.7,
    //   valueGetter: (params, row) => {
    //     if (row.source === "manual") {
    //       return row.projects_text || "—";
    //     }

    //     const projects = params;
    //     if (!Array.isArray(projects)) return "—";

    //     return projects.map((p) => p?.title ?? "").join(", ");
    //   },
    // },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      renderCell: (params) => {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            width="70%"
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
              type="button"
              onClick={() => {
                navigate(`/proposals/${params.row.id}`);
              }}
            >
              Details
            </Button>
          </Box>
        );
      },
    },
  ];

  const allFiles = selectedProposal
    ? [
        ...(selectedProposal.cna_std_rbs_files || []),
        ...(selectedProposal.cna_std_erda_files || []),
        ...(selectedProposal.cna_std_nra_files || []),
        ...(selectedProposal.cna_std_pixe_files || []),
        ...(selectedProposal.cna_std_pige_files || []),
        ...(selectedProposal.cmam_toferda_files || []),
        ...(selectedProposal.cna_ub_ibic_files || []),
        ...(selectedProposal.cna_ub_pixe_files || []),
        ...(selectedProposal.cna_ub_tribic_files || []),
        ...(selectedProposal.hispanos_neutron_files || []),
      ]
    : [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ height: "80vh", width: "100%", padding: "20px" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Box display="flex" gap={1.5}>
            <Button
              variant="contained"
              color="success"
              onClick={(e) => {
                e.currentTarget.blur(); // 🔥 clave
                setCreateOpen(true);
              }}
            >
              New Proposal
            </Button>

            <Button
              variant="outlined"
              color="inherit"
              startIcon={<DeleteOutlineIcon />}
              disabled={selectedProposals.length === 0}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          </Box>

          <CreateProposalDialog
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            onCreated={handleCreated}
            token={token}
          />

          <DeleteProposalDialog
            open={deleteOpen}
            proposals={selectedProposalObjects}
            onClose={() => setDeleteOpen(false)}
            handleDelete={handleDelete}
          />
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault(); // Evita recargar la página
              setAppliedSearch(searchQuery);
            }}
            sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}
          >
            <TextField
              label="Search proposals..."
              variant="outlined"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mr: 2 }}
              InputProps={{
                style: {
                  backgroundColor: "white",
                  borderRadius: 4,
                },
              }}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ mr: 2 }}
            onClick={() => setAppliedSearch(searchQuery)} // <-- Aplica el filtro
          >
            Search
          </Button>

          <Button
            variant="contained"
            color="error"
            sx={{ mr: 2 }}
            onClick={() => {
              setSearchQuery("");
              setAppliedSearch("");
              setFilteredProposals(proposals);
            }}
          >
            Clear
          </Button>

          <Button
            variant="contained"
            color="primary"
            sx={{ mr: 2 }}
            onClick={() => setDrawerOpen(true)}
          >
            Advanced search
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleExportSelected}
            disabled={selectedProposals.length === 0}
          >
            Export selected proposals
          </Button>
        </Box>

        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: { width: 300 }, // Puedes poner 500, 600, etc.
          }}
        >
          <ProposalFilterSidebar
            filters={filters}
            setFilters={setFilters}
            onApply={() => {
              console.log("Filters sent:", filters);
              fetchProposalsWithFilters();
              setDrawerOpen(false);
            }}
            onReset={() => {
              setFilters({});
              loadProposals();
            }}
          />
        </Drawer>

        <DataGrid
          rows={filteredProposals}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          pageSizeOptions={[30, 50, 100]}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(newSelection) => {
            setSelectedProposals(newSelection.map(Number));
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: "created_at", sort: "desc" }], // Orden en el que aparecen las propuestas
            },
            pagination: {
              paginationModel: { pageSize: 30, page: 0 },
            },
            // columns: {
            //   // Ocultar la columna de Created date
            //   columnVisibilityModel: {
            //     created_at: false,
            //   },
            // },
          }}
          sx={{
            backgroundColor: "#fff", // Fondo de las celdas
            border: "1px solid #e0e0e0",
            borderRadius: "8px",

            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#e3f2fd", // 💠 Celeste claro (puedes ajustar)
              color: "#000000", // Texto azul oscuro opcional
              fontWeight: "bold",
            },

            "& .MuiDataGrid-cell": {
              color: "#333", // Color del texto de las celdas
            },

            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f5f5f5", // Efecto hover suave
            },

            "& .MuiDataGrid-selectedRowCount": {
              visibility: "hidden", // Opcional: oculta el contador de seleccionados si no lo usas
            },
          }}
        />
      </Box>
    </DashboardLayout>
  );
};

export default ProposalManagement;
